// src/components/PartyAutoComplete.jsx
import React, { useState, forwardRef, useEffect, useRef, useImperativeHandle } from "react";
import { collection, getDocs, query, where } from "firebase/firestore";
import { db } from "../firebase";

/**
 * PartyAutoComplete
 * Props:
 * - selectedParty, setSelectedParty
 * - allowedGroup: string | string[] | null  (e.g. "Employee" or ["Employee","Sundry Debtors"])
 * - inputRef, onEnter, placeholder
 *
 * Behavior:
 * - tries server-side where() query if allowedGroup is a single string.
 * - if that fails (or returns 0), falls back to full collection fetch + client-side case-insensitive filter.
 * - supports parties stored in either "parties" or "customers" collections (tries parties first).
 */
const PartyAutoComplete = forwardRef(function PartyAutoComplete(
  {
    selectedParty,
    setSelectedParty,
    allowedGroup = null,
    inputRef = null,
    onEnter = null,
    placeholder = "Select Party...",
    className = "relative w-64",
  },
  forwardedRef
) {
  const [queryText, setQueryText] = useState("");
  const [partyList, setPartyList] = useState([]);
  const [open, setOpen] = useState(false);
  const [highlightIndex, setHighlightIndex] = useState(-1);
  const wrapperRef = useRef(null);
  const internalInputRef = useRef(null);
  const combinedRef = inputRef || forwardedRef || internalInputRef;
  const mountedRef = useRef(true);

  useImperativeHandle(forwardedRef, () => ({
    focus: () => {
      try { combinedRef.current?.focus?.(); } catch (e) {}
    },
    get value() { return combinedRef.current?.value; }
  }), [combinedRef]);

  useEffect(() => {
    mountedRef.current = true;
    return () => { mountedRef.current = false; };
  }, []);

  useEffect(() => {
    // reflect external selection into input text
    setQueryText(selectedParty?.partyName ?? selectedParty?.name ?? "");
  }, [selectedParty]);

  // normalize allowedGroup into an array of lowercase strings if provided
  const allowedGroupsNorm = (() => {
    if (!allowedGroup) return null;
    if (Array.isArray(allowedGroup)) return allowedGroup.map(g => String(g || "").toLowerCase());
    return [String(allowedGroup).toLowerCase()];
  })();

  // helper: check if a party matches allowedGroup criteria
  const partyMatchesGroup = (p) => {
    if (!allowedGroupsNorm) return true;
    const g = (p.group || p.groupName || p.group_type || p.group || "").toString().toLowerCase();
    // allow if any allowedGroup equals group's value OR if group array includes it
    if (allowedGroupsNorm.includes(g)) return true;
    // also allow if p.groups is an array that contains any allowedGroup
    if (Array.isArray(p.groups)) {
      for (const gg of p.groups) {
        if (allowedGroupsNorm.includes(String(gg || "").toLowerCase())) return true;
      }
    }
    return false;
  };

  // Core fetch: try parties collection first, then try customers collection as fallback
  useEffect(() => {
    let cancelled = false;

    const clientFilter = (list) => {
      // map and filter client-side with group logic
      return list.map(doc => {
        // normalize fields
        return {
          id: doc.id ?? doc.id,
          partyName: doc.partyName ?? doc.name ?? doc.partyName ?? "",
          phone: doc.phone ?? doc.mobile ?? "",
          group: doc.group ?? doc.groupName ?? "",
          raw: doc
        };
      }).filter(p => partyMatchesGroup(p));
    };

    const fetchFromCollection = async (colName) => {
      try {
        // If allowedGroupsNorm is a single group and not null, attempt server-side where query
        if (allowedGroupsNorm && allowedGroupsNorm.length === 1) {
          try {
            const serverQ = query(collection(db, colName), where("group", "==", allowedGroupsNorm[0]));
            const snap = await getDocs(serverQ);
            if (snap && snap.size > 0) {
              const list = snap.docs.map(d => ({ id: d.id, ...d.data() }));
              return list.map(l => ({
                id: l.id,
                partyName: l.partyName ?? l.name ?? "",
                phone: l.phone ?? l.mobile ?? "",
                group: l.group ?? l.groupName ?? "",
                raw: l
              }));
            }
            // if 0 results from server query, we'll fall back to full fetch below
          } catch (err) {
            // server-side query can fail if the client lacks index or permission — ignore and fallback
            console.warn(`server-side where() failed for ${colName}`, err);
          }
        }

        // fallback: fetch entire collection, then client-side filter
        const snapAll = await getDocs(collection(db, colName));
        const arr = snapAll.docs.map(d => ({ id: d.id, ...d.data() }));
        return arr.map(a => ({
          id: a.id,
          partyName: a.partyName ?? a.name ?? "",
          phone: a.phone ?? a.mobile ?? "",
          group: a.group ?? a.groupName ?? "",
          raw: a
        }));
      } catch (err) {
        console.error("Error fetching from", colName, err);
        throw err;
      }
    };

    const run = async () => {
      try {
        // try parties first
        let results = [];
        try {
          results = await fetchFromCollection("parties");
        } catch (err) {
          // if parties fetch fails (permission), try customers
          console.warn("fetch parties failed, trying customers", err);
        }

        if ((!results || results.length === 0)) {
          // try customers collection as fallback
          try {
            const custRes = await fetchFromCollection("customers");
            results = results.concat(custRes || []);
          } catch (err) {
            console.warn("fetch customers failed as fallback", err);
          }
        }

        // apply final client-side group filtering and dedupe by id/partyName
        const filtered = (results || []).filter(p => partyMatchesGroup(p));
        const unique = [];
        const seen = new Set();
        for (const p of filtered) {
          const key = `${p.id}:::${(p.partyName || "").toString().toLowerCase()}`;
          if (!seen.has(key)) { seen.add(key); unique.push(p); }
        }

        if (!cancelled && mountedRef.current) setPartyList(unique);
      } catch (err) {
        // top-level fetch error (likely permission issue)
        console.error("PartyAutoComplete fetch failed:", err);
        if (!cancelled) setPartyList([]); // empty list so UI shows "No results"
      }
    };

    run();
    return () => { cancelled = true; };
  }, [allowedGroup]); // refetch when allowedGroup changes

  // filtered view by query text
  const filtered = partyList.filter((p) =>
    (p.partyName || "").toLowerCase().includes((queryText || "").toLowerCase())
  );

  useEffect(() => {
    setHighlightIndex(filtered.length > 0 ? Math.max(0, Math.min(highlightIndex, filtered.length - 1)) : -1);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [filtered.length]);

  useEffect(() => {
    if (highlightIndex < 0) return;
    const ul = wrapperRef.current?.querySelector("ul");
    if (!ul) return;
    const li = ul.children[highlightIndex];
    if (li && typeof li.scrollIntoView === "function") {
      li.scrollIntoView({ block: "nearest", inline: "nearest" });
    }
  }, [highlightIndex]);

  useEffect(() => {
    const handleClickOutside = (e) => {
      if (wrapperRef.current && !wrapperRef.current.contains(e.target)) {
        setOpen(false);
        setHighlightIndex(-1);
      }
    };
    document.addEventListener("mousedown", handleClickOutside);
    return () => document.removeEventListener("mousedown", handleClickOutside);
  }, []);

  useEffect(() => {
    const onKey = (e) => {
      if (e.key === "Escape") {
        setOpen(false);
        setHighlightIndex(-1);
        return;
      }
    };
    window.addEventListener("keydown", onKey);
    return () => window.removeEventListener("keydown", onKey);
  }, []);

  const handleSelect = (p) => {
    setSelectedParty(p);
    setQueryText(p.partyName);
    setOpen(false);
    setHighlightIndex(-1);
    if (typeof onEnter === "function") setTimeout(() => onEnter(), 20);
  };

  const handleKeyDown = (e) => {
    if (!open && (e.key === "ArrowDown" || e.key === "ArrowUp")) {
      e.preventDefault();
      setOpen(true);
      setHighlightIndex(0);
      return;
    }

    if (e.key === "ArrowDown") {
      e.preventDefault();
      setHighlightIndex((prev) => (filtered.length === 0 ? -1 : (prev < filtered.length - 1 ? prev + 1 : 0)));
    } else if (e.key === "ArrowUp") {
      e.preventDefault();
      setHighlightIndex((prev) => (filtered.length === 0 ? -1 : (prev > 0 ? prev - 1 : filtered.length - 1)));
    } else if (e.key === "Enter") {
      e.preventDefault();
      if (open) {
        if (highlightIndex >= 0 && filtered[highlightIndex]) {
          handleSelect(filtered[highlightIndex]);
          return;
        }
        const found = filtered.find(
          (p) => (p.partyName || "").toLowerCase() === (queryText || "").toLowerCase()
        );
        if (found) { handleSelect(found); return; }
      }
      if (typeof onEnter === "function") setTimeout(() => onEnter(), 20);
    } else if (e.key === "Tab") {
      if (open && highlightIndex >= 0 && filtered[highlightIndex]) {
        handleSelect(filtered[highlightIndex]);
      } else {
        setOpen(false);
        setHighlightIndex(-1);
      }
    }
  };

  return (
    <div className={className} ref={wrapperRef}>
      <input
        ref={combinedRef}
        type="text"
        value={queryText}
        onFocus={() => { setOpen(true); if (filtered.length === 0) setHighlightIndex(-1); }}
        onChange={(e) => {
          setQueryText(e.target.value);
          setSelectedParty(null);
          setOpen(true);
          setHighlightIndex(-1);
        }}
        onKeyDown={handleKeyDown}
        placeholder={placeholder}
        aria-autocomplete="list"
        aria-haspopup="listbox"
        aria-activedescendant={
          open && highlightIndex >= 0 && filtered[highlightIndex]
            ? `party-${filtered[highlightIndex].id}`
            : undefined
        }
        className="w-full border px-2 py-1 rounded"
      />

      {open && (
        <ul
          role="listbox"
          className="absolute z-10 w-full bg-white border rounded mt-1 max-h-44 overflow-y-auto text-sm shadow"
        >
          {filtered.length === 0 ? (
            <li className="px-2 py-2 text-xs text-gray-500">No results</li>
          ) : (
            filtered.map((p, idx) => (
              <li
                id={`party-${p.id}`}
                key={p.id}
                role="option"
                aria-selected={idx === highlightIndex}
                className={`px-2 py-1 cursor-pointer ${idx === highlightIndex ? "bg-blue-500 text-white" : "hover:bg-gray-200"}`}
                onMouseEnter={() => setHighlightIndex(idx)}
                onMouseLeave={() => setHighlightIndex(-1)}
                onClick={() => handleSelect(p)}
              >
                <div className="font-medium">{p.partyName}</div>
                {p.phone && <div className="text-xs">{p.phone}</div>}
                {p.group && <div className="text-xs text-gray-400">{p.group}</div>}
              </li>
            ))
          )}
        </ul>
      )}
    </div>
  );
});

export default PartyAutoComplete;
